// K-Beauty Skincare Guide Section
// Displays clickable cards for each skincare step with detail popup

import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence, useDragControls, PanInfo } from 'framer-motion'
import { KBEAUTY_STEPS, type KBeautyStep } from '@/data/kbeautyGuide'

// AM/PM Routine Steps Data
const AM_ROUTINE_STEPS = [
  { name: 'Cleanser', icon: '🧴' },
  { name: 'Toner', icon: '💧' },
  { name: 'Serum', icon: '✨' },
  { name: 'Eye Cream', icon: '👁️' },
  { name: 'Moisturizer', icon: '🧴' },
  { name: 'Sunscreen', icon: '☀️' },
]

const PM_ROUTINE_STEPS = [
  { name: '1st Cleanser', icon: '🧴' },
  { name: '2nd Cleanser', icon: '🧴' },
  { name: 'Additional Care', icon: '✨' },
  { name: 'Sheet Mask', icon: '🎭' },
  { name: 'Serum', icon: '💧' },
  { name: 'Eye Cream', icon: '👁️' },
  { name: 'Moisturizer', icon: '🧴' },
  { name: 'Treatment', icon: '🌙' },
]

// Horizontal Routine Steps Scroller Component
function RoutineStepsScroller() {
  return (
    <div className="mt-4 space-y-4">
      {/* AM Routine */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🌅</span>
          <p className="text-sm font-semibold text-amber-800">Morning Routine</p>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
          {AM_ROUTINE_STEPS.map((step, index) => (
            <div key={step.name} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center bg-white rounded-lg px-3 py-2 border border-amber-200 min-w-[70px]">
                <span className="text-lg mb-0.5">{step.icon}</span>
                <span className="text-[10px] font-medium text-gray-700 text-center whitespace-nowrap">{step.name}</span>
              </div>
              {index < AM_ROUTINE_STEPS.length - 1 && (
                <span className="text-amber-400 mx-1 flex-shrink-0">→</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* PM Routine */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🌙</span>
          <p className="text-sm font-semibold text-indigo-800">Evening Routine</p>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
          {PM_ROUTINE_STEPS.map((step, index) => (
            <div key={step.name} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center bg-white rounded-lg px-3 py-2 border border-indigo-200 min-w-[70px]">
                <span className="text-lg mb-0.5">{step.icon}</span>
                <span className="text-[10px] font-medium text-gray-700 text-center whitespace-nowrap">{step.name}</span>
              </div>
              {index < PM_ROUTINE_STEPS.length - 1 && (
                <span className="text-indigo-400 mx-1 flex-shrink-0">→</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface KBeautyGuideProps {
  className?: string
}

export function KBeautyGuide({ className }: KBeautyGuideProps) {
  const [selectedStep, setSelectedStep] = useState<KBeautyStep | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [hasClickedStep, setHasClickedStep] = useState(false)

  const handleStepClick = (step: KBeautyStep) => {
    setHasClickedStep(true)
    setSelectedStep(step)
    setIsDetailOpen(true)
  }

  const handleNavigate = (step: KBeautyStep) => {
    setSelectedStep(step)
  }

  return (
    <div className={className}>
      <h2 className="text-lg font-semibold text-[#075D61] mb-2" style={{ fontFamily: "'Anybody', sans-serif" }}>
        K-Beauty Skincare Guide
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Master the Korean skincare routine - tap to learn more
      </p>

      {/* Step Cards */}
      <div className="grid grid-cols-2 gap-3">
        {KBEAUTY_STEPS.map((step, index) => (
          <div key={step.id} className="relative">
            {index === 0 && !hasClickedStep && (
              <motion.img
                src="/image/click.png"
                alt="Click to view details"
                className="absolute top-[70%] left-[calc(50%+28px)] -translate-x-1/2 -translate-y-[28%] w-[60px] h-auto z-30 pointer-events-none"
                animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.7, 0.5] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
            <button
              onClick={() => handleStepClick(step)}
              className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-left hover:border-[#46C0C6] hover:shadow-md transition-all active:scale-[0.98]"
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                  style={{ backgroundColor: `${step.color}20` }}
                >
                  {step.icon}
                </div>
                <p className="font-semibold text-gray-900">
                  Step {step.step}: {step.name}
                </p>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">
                {step.briefDescription}
              </p>
            </button>
          </div>
        ))}
      </div>

      {/* AM/PM Routine Quick Reference */}
      <RoutineStepsScroller />

      {/* Detail Sheet */}
      <KBeautyStepDetailSheet
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        step={selectedStep}
        allSteps={KBEAUTY_STEPS}
        onNavigate={handleNavigate}
      />
    </div>
  )
}

// Detail Sheet Component
interface KBeautyStepDetailSheetProps {
  isOpen: boolean
  onClose: () => void
  step: KBeautyStep | null
  allSteps: KBeautyStep[]
  onNavigate: (step: KBeautyStep) => void
}

function KBeautyStepDetailSheet({
  isOpen,
  onClose,
  step,
  allSteps,
  onNavigate
}: KBeautyStepDetailSheetProps) {
  const dragControls = useDragControls()
  const contentRef = useRef<HTMLDivElement>(null)
  const [direction, setDirection] = useState<'left' | 'right'>('right')

  // Scroll to top when step changes
  useEffect(() => {
    if (contentRef.current && step) {
      contentRef.current.scrollTop = 0
    }
  }, [step])

  // Navigation helpers
  const currentIndex = step ? allSteps.findIndex(s => s.id === step.id) : -1
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < allSteps.length - 1

  const goToPrev = useCallback(() => {
    if (hasPrev) {
      setDirection('left')
      onNavigate(allSteps[currentIndex - 1])
    }
  }, [hasPrev, allSteps, currentIndex, onNavigate])

  const goToNext = useCallback(() => {
    if (hasNext) {
      setDirection('right')
      onNavigate(allSteps[currentIndex + 1])
    }
  }, [hasNext, allSteps, currentIndex, onNavigate])

  // Keyboard navigation
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goToPrev()
      if (e.key === 'ArrowRight') goToNext()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose, goToPrev, goToNext])

  if (!step) return null

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.y > 100 || info.velocity.y > 500) {
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Sheet - max height 85vh to ensure X button is always visible */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl max-h-[85vh] overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <div
              className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{step.icon}</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Step {step.step}: {step.name}
                  </h2>
                  <p className="text-sm text-[#46C0C6]">K-Beauty Routine</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2.5 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Navigation buttons */}
            <>
              <button
                onClick={goToPrev}
                disabled={!hasPrev}
                className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
                  hasPrev
                    ? 'bg-white/90 hover:bg-white text-gray-800'
                    : 'bg-gray-200/50 text-gray-400 cursor-not-allowed'
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              <button
                onClick={goToNext}
                disabled={!hasNext}
                className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
                  hasNext
                    ? 'bg-white/90 hover:bg-white text-gray-800'
                    : 'bg-gray-200/50 text-gray-400 cursor-not-allowed'
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>

              {/* Navigation indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1">
                <span className="text-white text-xs font-medium">
                  {currentIndex + 1} / {allSteps.length}
                </span>
              </div>
            </>

            {/* Scrollable content - adjusted for 85vh sheet height */}
            <div ref={contentRef} className="overflow-y-auto max-h-[calc(85vh-120px)] pb-16 overflow-x-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={step.id}
                  initial={{ x: direction === 'right' ? 100 : -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: direction === 'right' ? -100 : 100, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="p-5 space-y-6"
                >
                  {/* Description */}
                  <div>
                    <p className="text-gray-600 leading-relaxed">{step.fullDescription}</p>
                  </div>

                  {/* AM/PM Routine */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-amber-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🌅</span>
                        <span className="font-semibold text-amber-800">Morning</span>
                      </div>
                      <p className="text-sm text-amber-700">{step.amPm.am}</p>
                    </div>
                    <div className="bg-indigo-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">🌙</span>
                        <span className="font-semibold text-indigo-800">Evening</span>
                      </div>
                      <p className="text-sm text-indigo-700">{step.amPm.pm}</p>
                    </div>
                  </div>

                  {/* Products */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">
                      Products in this Step
                    </h3>
                    <div className="space-y-3">
                      {step.products.map((product, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-xl p-4"
                        >
                          <p className="font-medium text-gray-900 mb-1">{product.name}</p>
                          {product.description && (
                            <p className="text-sm text-gray-500 mb-2">{product.description}</p>
                          )}
                          <div className="flex flex-wrap gap-1.5">
                            {product.types.map((type, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-white rounded-full text-xs text-gray-600 border border-gray-200"
                              >
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tips */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">
                      Pro Tips
                    </h3>
                    <div className="space-y-2">
                      {step.tips.map((tip, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="text-[#46C0C6] flex-shrink-0">💡</span>
                          <p className="text-sm text-gray-600">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
